import React, { useCallback, useEffect, useState } from 'react';

import { EmptyResourceMessage } from '@/components/shared/EmptyResourceMessage';
import {
	AsaphDialog,
	AsaphDialogHeader,
	AsaphDialogHeaderButton,
} from '@/components/shared/Dialog';
import { SongCard } from '@/components/shared/SongCard';
import { INITIAL_SONGLIST_RESULT_SIZE, PANEL_TRANSITION_TIMEOUT } from '@/config/constants';
import { Song, useSearchSongsMutation } from '@/graphql';
import { CircularProgress } from '@material-ui/core';
import { useSnackbar } from 'notistack';

import styles from './ImportMatchedSongs.module.scss';

export type ImportMatchedSongsProps = {
	songIds: string[];
	onClose: () => void;
	onSongOpen?: (songId: string) => void;
};

export const ImportMatchedSongs: React.FC<ImportMatchedSongsProps> = ({
	songIds,
	onClose,
	onSongOpen,
}) => {
	const { enqueueSnackbar } = useSnackbar();
	const {
		mutateAsync: searchSongs,
		isLoading,
		error,
	} = useSearchSongsMutation();

	const [results, setResults] = useState<Song[]>([]);
	const [totalCount, setTotalCount] = useState(0);
	const [page, setPage] = useState(0);
	const [loadMoreRef, setLoadMoreRef] = useState<HTMLDivElement | null>(null);

	useEffect(() => {
		async function load() {
			if (!songIds.length) {
				setResults([]);
				setTotalCount(0);
				return;
			}

			const searchResults = await searchSongs({
				filters: {
					term: '',
					tagsByCategory: null,
					onlySongIds: songIds,
				},
				input: {
					limit: INITIAL_SONGLIST_RESULT_SIZE,
					offset: page * INITIAL_SONGLIST_RESULT_SIZE,
				},
				excludeMyTeamSongs: false,
			});

			const items = searchResults?.searchSongs?.items ?? [];
			setTotalCount(searchResults?.searchSongs?.totalCount ?? 0);
			setResults((prev) =>
				page === 0
					? items
					: [...prev, ...items].reduce<Song[]>((acc, song) => {
							if (!acc.find(({ id }) => song.id === id)) {
								return [...acc, song];
							}
							return acc;
					  }, []),
			);
		}

		void load();
		// songIds identity is stable for a mounted instance (parent remounts via key)
		// eslint-disable-next-line react-hooks/exhaustive-deps
	}, [page, searchSongs]);

	const handleLoadMoreVisible = useCallback(() => {
		if (!isLoading && results.length < totalCount) {
			setPage((current) => current + 1);
		}
	}, [isLoading, results.length, totalCount]);

	useEffect(() => {
		let observer: IntersectionObserver | undefined;

		if (loadMoreRef !== null) {
			observer = new IntersectionObserver(([entry]) => {
				if (entry.isIntersecting) {
					handleLoadMoreVisible();
				}
			});
			observer.observe(loadMoreRef);
		}

		return () => observer?.disconnect();
	}, [loadMoreRef, handleLoadMoreVisible]);

	useEffect(() => {
		if (error) {
			console.error(error);
			enqueueSnackbar('Error while loading matched songs', {
				variant: 'error',
			});
		}
	}, [error, enqueueSnackbar]);

	return (
		<AsaphDialog open={true} onClose={onClose} paperClassName={styles['panel']}>
			<div className={styles['toolbar']}>
				<AsaphDialogHeader
					leadingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: (
							<AsaphDialogHeaderButton
								type="text"
								label="Done"
								handlePress={onClose}
							/>
						),
					}}
					title={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						label: 'Matched songs',
					}}
					trailingButton={{
						needsFadeIn: true,
						fadeTimeout: PANEL_TRANSITION_TIMEOUT,
						component: '',
					}}
				/>
			</div>
			<section className={styles['content']}>
				<div className={styles['meta']}>
					Showing {totalCount || songIds.length} song
					{(totalCount || songIds.length) === 1 ? '' : 's'}
				</div>
				<div className={styles['list']}>
					{results.map((song) => (
						<SongCard
							key={song.id}
							song={song}
							displayKey={false}
							selectable={false}
							negativeSpacing
							onClick={
								onSongOpen
									? () => onSongOpen(song.id)
									: undefined
							}
						/>
					))}
					{results.length < totalCount && (
						<div
							ref={(newRef) => setLoadMoreRef(newRef)}
							className={styles['loading']}
						>
							<CircularProgress size={28} />
						</div>
					)}
					{isLoading && !results.length && (
						<div className={styles['loading']}>
							<CircularProgress />
						</div>
					)}
					{!isLoading && !results.length && (
						<div className={styles['empty']}>
							<EmptyResourceMessage
								title="No songs"
								subtitle="No matched songs were found"
							/>
						</div>
					)}
				</div>
			</section>
		</AsaphDialog>
	);
};

export default ImportMatchedSongs;
